import { Button } from 'components/atoms';
import TitleCard from 'components/molecules/card/title/TitleCard';
import ViewHint from 'components/molecules/hint/ViewHint';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPaperPlaneTilt, PiTranslate } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { ITitle } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

export const externalInitialValues: ITitle = {
  title: '',
  version: '',
  language: -1,
};

type ParentType = 'TRACK' | 'VIDEO' | 'MUSIC_PRODUCT_DIGITAL' | 'MUSIC_PRODUCT_PHYSICAL';

interface ITitlesTabProps {
  propertyName?: 'otherTitles';
  parent: ParentType;
}

const NAVIGATION_MAP = {
  MUSIC_PRODUCT_DIGITAL: {
    storeKey: 'musicProductDigitalForm' as const,
    basePath: 'music-product-digital',
    back: 'data',
    hint: 'music-product-digital-titles',
    next: 'delivery',
    isLast: true,
    nextIcon: undefined,
  },
  MUSIC_PRODUCT_PHYSICAL: {
    storeKey: 'musicProductPhysicalForm' as const,
    basePath: 'music-product-physical',
    next: 'external',
    back: 'data',
    hint: 'music-product-physical-titles',
    isLast: false,
    nextIcon: <PiPaperPlaneTilt />,
  },
  TRACK: {
    storeKey: 'trackForm' as const,
    basePath: 'track',
    next: 'external',
    back: 'data',
    hint: 'track-titles',
    isLast: false,
    nextIcon: <PiPaperPlaneTilt />,
  },
  VIDEO: {
    storeKey: 'videoForm' as const,
    basePath: 'video',
    next: 'external',
    back: 'data',
    hint: 'video-titles',
    isLast: false,
    nextIcon: <PiPaperPlaneTilt />,
  },
};

const TitlesTab = <Form extends Record<'otherTitles', ITitle[]> & Record<'id', string>>({
  propertyName = 'otherTitles',
  parent,
}: ITitlesTabProps) => {
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setForm, musicProductDigitalForm, musicProductPhysicalForm } = useStoreState();

  const config = NAVIGATION_MAP[parent];

  const handleNavigate = (direction: 'next' | 'back') => {
    if (!config) return;

    setForm(config.storeKey, values as any);

    const subPath = config[direction];
    const action = values.id ? `edit/${values.id}` : 'create';

    navigate(`/${config.basePath}/${action}/${subPath}`);
  };

  const lastName = useMemo(() => {
    if (
      (parent === 'TRACK' || parent === 'VIDEO') &&
      (musicProductDigitalForm || musicProductPhysicalForm)
    ) {
      return 'save-and-back';
    }

    if (parent === 'MUSIC_PRODUCT_DIGITAL') {
      return 'delivery';
    }

    return 'send-to-verification';
  }, [musicProductDigitalForm, musicProductPhysicalForm, parent]);

  return (
    <StepsTab
      isLast={config.isLast}
      nextName={config.isLast ? lastName : config?.next}
      nextIcon={config.isLast ? config?.nextIcon : undefined}
      backName={config?.back}
      onNext={() => !config.isLast && handleNavigate('next')}
      onBack={() => handleNavigate('back')}
    >
      <ViewHint hintId={config?.hint} />
      {!status?.displayMode && (
        <Button
          fullWidth
          variant={BUTTON_VARIANT.white}
          onClick={() =>
            setFieldValue(propertyName, [externalInitialValues, ...values[propertyName]])
          }
          size="large"
        >
          <Button.Icon icon={<PiTranslate />} />
          <Button.Text text={t('button.translate')} />
        </Button>
      )}
      <ListWrapper
        isLoading={status?.isLoading}
        items={values[propertyName]}
        item={(title, index) => (
          <TitleCard
            key={index}
            index={index}
            title={title}
            name={propertyName}
            displayMode={status?.displayMode}
            onRemove={() =>
              setFieldValue(
                propertyName,
                values[propertyName].filter((_, titleIndex) => index !== titleIndex),
              )
            }
          />
        )}
      />
    </StepsTab>
  );
};

export default TitlesTab;
